import {
  ReferralDocumentType,
  ReferralStoredDocument,
  getReferralDocumentFileRef,
  singletonReferralDocumentTypes,
} from '@packages/core-shared';
import { AlertError, Button } from '@percihealth/react';
import { ref, uploadBytes } from 'firebase/storage';
import { useRef, useState } from 'react';
import { useFirebase } from '../../context';
import UploadIcon from '../icons/UploadIcon';

interface Props {
  referralId: string;
  type: ReferralDocumentType;
  extensions: string;
  // How many more documents of this kind the referral may hold: 0 disables the
  // button, more than one lets the referrer pick several files at once. A
  // singleton type (the consent form, the clinical report) is always one — it
  // replaces the one that is there.
  slotsLeft?: number;
  // Everything uploaded in one go, in one call: the forms build the next
  // documents array from the current one, so a call per file would drop all
  // but the last.
  onUploaded: (documents: ReferralStoredDocument[]) => void;
}

// Uploads a referral's supporting documents straight to Firebase Storage,
// under the referral's own folder — the same layout the API gives a document
// sent inline in the body (PPL-3549), so a referral's files live in one place
// either way. Going straight to storage means a file is bounded only by
// storage.rules (50MB), not by the API's request body limit.
export const DocumentUpload = ({
  referralId,
  type,
  extensions,
  slotsLeft,
  onUploaded,
}: Props) => {
  const { storage } = useFirebase();
  const uploadRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isSingleton = singletonReferralDocumentTypes.includes(type);
  const slots = isSingleton ? 1 : (slotsLeft ?? 1);

  const uploadAll = async (files: File[]) => {
    setUploading(true);
    setErrorMessage(null);

    // Never silently drop what the referrer picked: upload what fits and say
    // what did not.
    const accepted = files.slice(0, slots);
    const skipped = files.length - accepted.length;
    const uploaded: ReferralStoredDocument[] = [];

    for (const file of accepted) {
      const path = getReferralDocumentFileRef({
        referralId,
        type,
        fileName: file.name,
        uploadedAt: new Date(),
      });

      try {
        await uploadBytes(ref(storage, path), file);
        uploaded.push({ type, name: file.name, path });
      } catch (err) {
        console.error(err);
        setErrorMessage(`Cannot upload the file ${file.name}`);
        break;
      }
    }

    if (uploaded.length > 0) {
      onUploaded(uploaded);
    }
    if (skipped > 0) {
      setErrorMessage(
        `Only ${slots} more document${slots === 1 ? '' : 's'} can be attached — ${skipped} file${skipped === 1 ? ' was' : 's were'} not uploaded`,
      );
    }

    setUploading(false);
  };

  return (
    <div>
      <input
        type="file"
        accept={extensions}
        multiple={slots > 1}
        ref={uploadRef}
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          // let the same file be picked again after a remove
          e.target.value = '';
          if (files.length > 0) {
            uploadAll(files);
          }
        }}
      />
      <Button
        icon={<UploadIcon />}
        onClick={(e) => {
          e.preventDefault();
          uploadRef.current?.click();
        }}
        disabled={!referralId || slots < 1}
        submitting={uploading}
      >
        Upload
      </Button>
      {errorMessage && <AlertError>{errorMessage}</AlertError>}
    </div>
  );
};
